import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCan, useClaims } from '../../lib/api/hooks';
import { TOUR_STEPS, type TourStep } from './steps';
import { TourContext, type TourApi } from './context';
import { TourOverlay } from './TourOverlay';

interface TourFlags {
  /** The first-login offer has been shown once (so we never pop it again). */
  offered: boolean;
  /** Tour was completed or explicitly dismissed — stop surfacing the reminder. */
  done: boolean;
}

const DEFAULT_FLAGS: TourFlags = { offered: false, done: false };

function readFlags(key: string): TourFlags {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_FLAGS;
    const parsed = JSON.parse(raw) as Partial<TourFlags>;
    return { offered: Boolean(parsed.offered), done: Boolean(parsed.done) };
  } catch {
    return DEFAULT_FLAGS; // private mode / corrupt value — behave like a first visit
  }
}

/**
 * Drives the guided tour and the first-login offer. Must sit inside the router and above the app
 * shell, since the nav components it coordinates with (held-open dropdowns) consume this context.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const can = useCan();
  const claims = useClaims();
  const navigate = useNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState<readonly TourStep[]>([]);
  const [index, setIndex] = useState(-1);
  const [flags, setFlags] = useState<TourFlags>(DEFAULT_FLAGS);
  const [offering, setOffering] = useState(false);

  // Tour progress is per-user, not per-browser: a shared workstation shouldn't hide the tour from
  // the next person to sign in.
  const sub = typeof claims.data?.['sub'] === 'string' ? (claims.data['sub'] as string) : null;
  const storageKey = sub ? `eftc.tour.v1.${sub}` : null;
  const hasAccount = Boolean(claims.data?.['custom:accountId']);

  useEffect(() => {
    if (storageKey) setFlags(readFlags(storageKey));
  }, [storageKey]);

  const persist = useCallback((patch: Partial<TourFlags>) => {
    setFlags((prev) => {
      const nextFlags = { ...prev, ...patch };
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(nextFlags));
        } catch {
          // Non-fatal: the tour just re-offers on the next visit.
        }
      }
      return nextFlags;
    });
  }, [storageKey]);

  const step = index >= 0 && index < steps.length ? (steps[index] as TourStep) : null;
  const total = steps.length;

  const start = useCallback(() => {
    setSteps(TOUR_STEPS.filter((s) => !s.capability || can(s.capability)));
    setIndex(0);
    setOffering(false);
    persist({ offered: true });
  }, [can, persist]);

  const stop = useCallback((completed: boolean) => {
    setIndex(-1);
    setSteps([]);
    persist({ offered: true, done: true });
    void completed; // finishing and skipping are recorded the same way today
  }, [persist]);

  const next = useCallback(() => {
    setIndex((i) => {
      if (i < 0) return i;
      if (i + 1 >= steps.length) {
        persist({ offered: true, done: true });
        setSteps([]);
        return -1;
      }
      return i + 1;
    });
  }, [steps.length, persist]);

  const back = useCallback(() => setIndex((i) => (i > 0 ? i - 1 : i)), []);

  const dismiss = useCallback(() => {
    setOffering(false);
    persist({ offered: true, done: true });
  }, [persist]);

  // First login: offer the tour once, after a beat so the dashboard has painted. Suppressed until
  // the account exists (the setup form owns the screen before that) and never over a live capture.
  useEffect(() => {
    if (!storageKey || !hasAccount) return;
    if (flags.offered || flags.done) return;
    if (location.pathname.startsWith('/app/capture/')) return;
    const t = setTimeout(() => setOffering(true), 700);
    return () => clearTimeout(t);
  }, [storageKey, hasAccount, flags.offered, flags.done, location.pathname]);

  // Some steps only make sense on a particular page — get there before spotlighting.
  useEffect(() => {
    if (step?.route && location.pathname !== step.route) navigate(step.route);
  }, [step, location.pathname, navigate]);

  const api = useMemo<TourApi>(() => ({
    step,
    index,
    total,
    openMenu: step?.menu ?? null,
    done: flags.done,
    start,
    next,
    back,
    stop,
    skip: next,
    dismiss,
  }), [step, index, total, flags.done, start, next, back, stop, dismiss]);

  return (
    <TourContext.Provider value={api}>
      {children}
      <TourOverlay />
      {offering && !step && <TourOffer onAccept={start} onDecline={dismiss} />}
    </TourContext.Provider>
  );
}

/** First-login prompt. Declining hides the reminder for good; the tour stays replayable from Resources. */
function TourOffer({ onAccept, onDecline }: { onAccept: () => void; onDecline: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDecline(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDecline]);

  return (
    <div className="confirm-overlay">
      <div className="confirm-card" role="dialog" aria-modal="true" aria-labelledby="tour-offer-title">
        <h3 className="confirm-title" id="tour-offer-title">New to EFTCreator?</h3>
        <div className="confirm-body">
          <p>
            Take a short guided tour — it points out where to install the Device Service, capture a
            transaction, and track your submissions. About a minute.
          </p>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: 0 }}>
            You can replay it later from Resources → Getting started.
          </p>
        </div>
        <div className="confirm-actions">
          <button className="btn btn-ghost" onClick={onDecline}>No thanks</button>
          <button className="btn btn-primary" onClick={onAccept}>Take the tour</button>
        </div>
      </div>
    </div>
  );
}
